import { FC, useState, useRef, useEffect } from 'react';
import { Formik, FormikProps } from 'formik';
import { Box, Modal, Typography, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Form } from 'react-router-dom';

import styles from './CreateIdeaStopModal.module.scss';
import { CreateIdeaStopFormData, IdeaStopType, IdeaStop } from '../../../../../types/ideaStop.types';
import { IDEA_STOP_INITIAL_VALUES } from '../../../../../types/ideaStop.initialValues';
import { validateEndDateTimeAfterStart } from '../../../../../utils/dateUtils';
import { validatePhoneNumber } from '../../../../../utils/phoneValidation';
import {
  getFieldsForIdeaStop,
  getIdeaStopUiFieldFromApiAttr,
  isIdeaStopFieldMandatory,
  isIdeaStopMandatoryFieldValueFilled,
} from '../../../../../types/ideaStop.config';
import { useCreateIdeaStopMutation, useUpdateIdeaStopMutation } from '../../../../../store/services/ideasApi';
import CustomSelect from '../../../../../common/CustomSelect';
import CustomButton from '../../../../../common/CustomButton';
import { useIdeaStopPayload } from './hooks/useIdeaStopPayload';
import { useIdeaStopTypes } from './hooks/useIdeaStopTypes';
import { useIdeaStopToFormData } from './hooks/useIdeaStopToFormData';
import MandatoryFields from '../MandatoryFields/MandatoryFields';
import OptionalFields from '../OptionalFields/OptionalFields';

const FORM_FIELD_KEYS = new Set(Object.keys(IDEA_STOP_INITIAL_VALUES));
const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

type ApiValidationItem = { attr?: string; detail?: string; code?: string };

const partitionApiValidationErrors = (
  errors: ApiValidationItem[],
  ideaStopType: string
): { fieldErrors: Record<string, string>; general: string[] } => {
  const fieldErrors: Record<string, string> = {};
  const general: string[] = [];

  for (const err of errors) {
    const detail = err.detail?.trim();
    if (!detail) continue;

    const apiAttr = err.attr;
    if (!apiAttr) {
      general.push(detail);
      continue;
    }

    const uiKey = getIdeaStopUiFieldFromApiAttr(apiAttr);
    if (uiKey && FORM_FIELD_KEYS.has(uiKey) && uiKey !== 'ideaStopType') {
      fieldErrors[uiKey] = fieldErrors[uiKey] ? `${fieldErrors[uiKey]} ${detail}` : detail;
    } else {
      general.push(detail);
    }
  }

  return { fieldErrors, general };
};

type CreateIdeaStopModalProps = {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  ideaId: number;
  editMode?: boolean;
  ideaStopData?: IdeaStop;
};

const CreateIdeaStopModal: FC<CreateIdeaStopModalProps> = ({
  open,
  onClose,
  onSuccess,
  ideaId,
  editMode = false,
  ideaStopData,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [resErrors, setResErrors] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [optionalAccordionOpen, setOptionalAccordionOpen] = useState(false);
  const [previousIdeaStopType, setPreviousIdeaStopType] = useState<IdeaStopType | ''>('');
  const { ideaStopTypes, isLoading: isTypesLoading, hasTypes } = useIdeaStopTypes();

  const formikRef = useRef<FormikProps<CreateIdeaStopFormData>>(null);

  const [createIdeaStop] = useCreateIdeaStopMutation();
  const [updateIdeaStop] = useUpdateIdeaStopMutation();

  const { convertToApiPayload } = useIdeaStopPayload(ideaId);
  const initialFormData = useIdeaStopToFormData(editMode ? ideaStopData : undefined);

  useEffect(() => {
    if (open) {
      setOptionalAccordionOpen(false);
    }
  }, [open]);

  const handleSubmit = async (values: CreateIdeaStopFormData) => {
    try {
      setIsLoading(true);
      setResErrors(null);

      if (editMode && ideaStopData) {
        const payload = convertToApiPayload(values);
        console.log('Updating idea stop with payload:', payload);

        await updateIdeaStop({
          id: ideaStopData.id,
          data: payload.ideaStopData,
        }).unwrap();

        console.log('Idea stop updated successfully');
      } else {
        const payload = convertToApiPayload(values);
        console.log('Creating idea stop with payload:', payload);

        await createIdeaStop(payload).unwrap();
        console.log('Idea stop created successfully');
      }

      setIsSuccess(true);
      onSuccess?.();
      handleClose();
    } catch (error: any) {
      console.error(`Error ${editMode ? 'updating' : 'creating'} idea stop:`, error);

      const formik = formikRef.current;
      const rawErrors = error?.data?.errors;

      if (Array.isArray(rawErrors) && formik) {
        const { fieldErrors, general } = partitionApiValidationErrors(rawErrors, formik.values.ideaStopType);

        for (const [key, message] of Object.entries(fieldErrors)) {
          formik.setFieldError(key as keyof CreateIdeaStopFormData, message);
          formik.setFieldTouched(key as keyof CreateIdeaStopFormData, true, false);
        }

        const optionalFields = new Set(getFieldsForIdeaStop(formik.values.ideaStopType).optional);
        if (Object.keys(fieldErrors).some(k => optionalFields.has(k))) {
          setOptionalAccordionOpen(true);
        }

        if (general.length > 0) {
          setResErrors(general.join('\n'));
        } else if (Object.keys(fieldErrors).length === 0) {
          setResErrors('Validation error occurred. Please check your input.');
        } else {
          setResErrors(null);
        }
      } else if (Array.isArray(rawErrors)) {
        const errorMessages = rawErrors.map((err: { detail?: string }) => err.detail).filter(Boolean) as string[];

        if (errorMessages.length > 0) {
          setResErrors(errorMessages.length > 1 ? errorMessages.map((msg, i) => `${i + 1}. ${msg}`).join('\n') : errorMessages[0]);
        } else {
          setResErrors('Validation error occurred. Please check your input.');
        }
      } else if (error?.data?.message) {
        setResErrors(error.data.message);
      } else if (error?.data?.detail) {
        setResErrors(error.data.detail);
      } else if (error?.message) {
        setResErrors(error.message);
      } else {
        setResErrors(`Failed to ${editMode ? 'update' : 'create'} idea stop. Please try again.`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const formikValidate = (values: CreateIdeaStopFormData) => {
    const errors: Partial<Record<keyof CreateIdeaStopFormData, string>> = {};

    if (!values.ideaStopType) {
      errors.ideaStopType = 'Idea stop type is required';
    }

    if (!values.description || values.description.trim() === '') {
      errors.description = 'Description is required';
    }

    if (values.ideaStopType) {
      if (isIdeaStopFieldMandatory('name', values.ideaStopType) && !values.name) {
        errors.name = 'Name is required';
      }

      if (isIdeaStopFieldMandatory('place', values.ideaStopType) && !values.place) {
        errors.place = 'Place is required';
      }

      if (isIdeaStopFieldMandatory('countryFrom', values.ideaStopType) && !values.countryFrom) {
        errors.countryFrom = 'Country from is required';
      }

      if (isIdeaStopFieldMandatory('countryTo', values.ideaStopType) && !values.countryTo) {
        errors.countryTo = 'Country to is required';
      }

      if (isIdeaStopFieldMandatory('vehicleType', values.ideaStopType) && !values.vehicleType) {
        errors.vehicleType = 'Vehicle type is required';
      }

      if (
        isIdeaStopFieldMandatory('flightFrom', values.ideaStopType) &&
        !isIdeaStopMandatoryFieldValueFilled(values, 'flightFrom')
      ) {
        errors.flightFrom = 'Flight from is required';
      }

      if (
        isIdeaStopFieldMandatory('flightTo', values.ideaStopType) &&
        !isIdeaStopMandatoryFieldValueFilled(values, 'flightTo')
      ) {
        errors.flightTo = 'Flight to is required';
      }
    }

    if (values.email && values.email.trim() !== '') {
      if (!EMAIL_REGEX.test(values.email.trim())) {
        errors.email = 'Invalid email address';
      }
    }

    if (values.phone && values.phone.trim() !== '') {
      if (!validatePhoneNumber(values.phone)) {
        errors.phone = 'Please enter a valid phone number (with country code, e.g. +1…)';
      }
    }

    if (values.link && values.link.trim() !== '') {
      const urlRegex = /^https?:\/\/[^\s]+$/;
      if (!urlRegex.test(values.link)) {
        errors.link = 'Link must start with http:// or https://';
      }
    }

    const applyRangeErrors = (
      startDate?: string,
      startTime?: string,
      endDate?: string,
      endTime?: string,
      endDateKey: keyof CreateIdeaStopFormData = 'endDate',
      endTimeKey: keyof CreateIdeaStopFormData = 'endTime'
    ) => {
      const rangeErrors = validateEndDateTimeAfterStart(startDate, startTime, endDate, endTime);
      if (rangeErrors.endDate && !errors[endDateKey]) {
        errors[endDateKey] = rangeErrors.endDate;
      }
      if (rangeErrors.endTime && !errors[endTimeKey]) {
        errors[endTimeKey] = rangeErrors.endTime;
      }
    };

    applyRangeErrors(values.startDate, values.startTime, values.endDate, values.endTime);
    applyRangeErrors(
      values.checkInDate,
      values.checkInTime,
      values.checkOutDate,
      values.checkOutTime,
      'checkOutDate',
      'checkOutTime'
    );
    applyRangeErrors(
      values.departureDate,
      values.departureTime,
      values.arrivalDate,
      values.arrivalTime,
      'arrivalDate',
      'arrivalTime'
    );

    return errors;
  };

  const handleClose = () => {
    onClose();
    setResErrors(null);
    setIsSuccess(false);
    setOptionalAccordionOpen(false);
    setPreviousIdeaStopType('');
    if (formikRef.current) {
      formikRef.current.resetForm();
    }
  };

  const handleIdeaStopTypeChange = (event: any, formik: FormikProps<CreateIdeaStopFormData>) => {
    const newIdeaStopType = event.target.value as IdeaStopType;

    if (newIdeaStopType !== previousIdeaStopType && previousIdeaStopType !== '') {
      const newValues = {
        ...IDEA_STOP_INITIAL_VALUES,
        ideaStopType: newIdeaStopType,
        description: formik.values.description,
      };
      formik.resetForm({ values: newValues });
    } else {
      formik.handleChange(event);
    }

    setPreviousIdeaStopType(newIdeaStopType);
  };

  if (isTypesLoading && !hasTypes) {
    return (
      <Modal
        open={open}
        onClose={handleClose}
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(34, 43, 58, 0.2)',
          },
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            borderRadius: '20px',
            p: 4,
            textAlign: 'center',
          }}
        >
          <Typography variant="h6">Loading...</Typography>
        </Box>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="create-idea-stop-modal"
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(34, 43, 58, 0.2)',
        },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          maxWidth: '600px',
          minWidth: 375,
          width: '100%',
          bgcolor: 'background.paper',
          borderRadius: '20px',
          p: '32px 64px',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          boxShadow: 'none',
          maxHeight: 'min(90vh, 90dvh)',
          '@media (max-width: 900px)': {
            p: '16px 32px',
            top: 'auto',
            bottom: 0,
            left: 0,
            right: 0,
            transform: 'none',
            maxWidth: '100%',
            minWidth: 0,
            width: '100%',
            borderRadius: '20px 20px 0 0',
            maxHeight: 'min(92vh, 92dvh)',
          },
        }}
      >
        <Box sx={{ position: 'absolute', top: 16, right: 20, cursor: 'pointer' }}>
          <CloseIcon
            sx={{ width: '30px', height: '30px' }}
            onClick={handleClose}
          />
        </Box>

        <Box className={styles.modalContent}>
          <Typography
            variant="h5Bold"
            sx={{ textAlign: 'center', mb: 3 }}
            color="#222B3A"
          >
            Details
          </Typography>

          <Formik
            innerRef={formikRef}
            initialValues={initialFormData}
            validate={formikValidate}
            validateOnChange
            validateOnBlur
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {formik => {
              const { values, handleSubmit } = formik;
              const canSubmit = Object.keys(formikValidate(values)).length === 0;

              return (
                <Form
                  className={styles.ideaStopForm}
                  onSubmit={handleSubmit}
                >
                  <Accordion
                    defaultExpanded
                    className={styles.accordion}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="h9Bold" color="#222B3A">Mandatory</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box className={styles.formSection}>
                        <CustomSelect
                          name="ideaStopType"
                          inputLabel="Activity Type"
                          value={values.ideaStopType || ''}
                          handleChange={e => handleIdeaStopTypeChange(e, formik)}
                          data={ideaStopTypes}
                          disabled={editMode}
                        />

                        {values.ideaStopType && <MandatoryFields formik={formik} />}
                      </Box>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion
                    className={styles.accordion}
                    expanded={optionalAccordionOpen}
                    onChange={(_, expanded) => setOptionalAccordionOpen(expanded)}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="h9Bold" color="#222B3A">Optional</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box className={styles.formSection}>
                        {values.ideaStopType && <OptionalFields formik={formik} />}
                      </Box>
                    </AccordionDetails>
                  </Accordion>

                  <Box className={styles.modalActions}>
                    <CustomButton
                      btnType="button"
                      variant="outlined"
                      onClick={handleClose}
                      disabled={isLoading}
                    >
                      Cancel
                    </CustomButton>
                    <CustomButton
                      btnType="submit"
                      variant="contained"
                      disabled={isLoading || !canSubmit}
                    >
                      {isLoading
                        ? editMode
                          ? 'Updating...'
                          : 'Creating...'
                        : editMode
                          ? 'Save changes'
                          : 'Save'}
                    </CustomButton>
                  </Box>

                  {isSuccess && (
                    <Typography
                      variant="h12"
                      sx={{ color: '#208B64', textAlign: 'center', mt: 2 }}
                    >
                      Idea stop {editMode ? 'updated' : 'created'} successfully!
                    </Typography>
                  )}

                  {resErrors && (
                    <Typography
                      variant="h12"
                      sx={{
                        color: '#C8102E',
                        textAlign: 'center',
                        mt: 2,
                        whiteSpace: 'pre-line',
                      }}
                    >
                      {resErrors}
                    </Typography>
                  )}
                </Form>
              );
            }}
          </Formik>
        </Box>
      </Box>
    </Modal>
  );
};

export default CreateIdeaStopModal;
